import config from '../config';
import { centToCurrency } from './formatNumber';

const roles = config.roles;

/**
 * Returns the username associated with the given role name from the provided data object.
 *
 * This function extracts the `name` property of a related entity (merchant, provider, agent)
 * based on the given role name.
 *
 * If the role is `'appz'`, it returns a joined string of all available names (merchant, provider, agent),
 * separated by newlines. For other roles, it returns the name corresponding to the role.
 *
 * @function getUserNameByRole
 * @param {string} role - The role object containing the name of the role.
 * @param {Object} data - The data object representing the full row or entity.
 * @param {Object} [data.merchant] - Merchant object (if present).
 * @param {string} [data.merchant.name] - Name of the merchant.
 * @param {Object} [data.provider] - Provider/PSP object (if present).
 * @param {string} [data.provider.name] - Name of the provider.
 * @param {Object} [data.agent] - Agent object (if present).
 * @param {string} [data.agent.name] - Name of the agent.
 * @returns {string} The resolved name based on the role, or a newline-joined string if role is `'appz'`.
 *
 * @example
 * getUserNameByRole({ name: 'merchant' }, { merchant: { name: 'John Doe' } }); // → "John Doe"
 * getUserNameByRole({ name: 'appz' }, { merchant: { name: 'John Doe' }, provider: { name: 'Jane Smith' } }); // → "John Doe\nJane Smith"
 * getUserNameByRole({ name: 'unknown' }, { merchant: { name: 'John Doe' } }); // → ""
 */
export const getUserNameByRole = (role, data) => {
    if (role?.systemRole === roles.appz.systemRole) {
        const users = [
            data?.merchant?.name,
            data?.provider?.name,
            data?.agent?.name
        ].filter(Boolean);

        return users.join('\n');
    }

    const map = {
        [roles.merchant.systemRole]: data?.merchant?.name,
        [roles.psp.systemRole]: data?.provider?.name,
        [roles.agent.systemRole]: data?.agent?.name,
        [roles.appz.systemRole]: ''
    };

    return map[role?.systemRole] ?? '';
};

/**
 * Retrieves the UUID of a transaction based on its kind.
 *
 * Supported kinds are:
 * - `'payment'`: returns `data.payment.uuid`
 * - `'p2p'`: returns `data.p2p.uuid`
 * - `'payout'`: returns `data.payout.uuid`
 *
 * If the kind does not match any of the supported transaction types,
 * an empty string is returned.
 *
 * @function getUuidTransactionByKind
 * @param {string} kind - The type of transaction (`'payment'` | `'p2p'` | `'payout'`).
 *                        This is a string identifying the transaction category.
 * @param {Object} data - The full transaction data object.
 * @param {Object} [data.payment] - Payment transaction data.
 * @param {string} [data.payment.uuid] - UUID of the payment transaction.
 * @param {Object} [data.p2p] - P2P transaction data.
 * @param {string} [data.p2p.uuid] - UUID of the P2P transaction.
 * @param {Object} [data.payout] - Payout transaction data.
 * @param {string} [data.payout.uuid] - UUID of the payout transaction.
 * @returns {string} The UUID string corresponding to the specified kind, or an empty string if unavailable.
 *
 * @example
 * getUuidTransactionByKind('payment', transactionData); // → "a1b2c3d4..."
 * getUuidTransactionByKind('p2p', transactionData);     // → "d4c3b2a1..."
 * getUuidTransactionByKind('invalid', transactionData); // → ""
 */
export const getUuidTransactionByKind = (kind, data) => {
    const map = {
        payment: data?.payment?.uuid,
        p2p: data?.p2p?.uuid,
        payout: data?.payout?.uuid
    };
    return map[kind] ?? '';
};

export const getCostType = (type, data) => {
    const agentDisplay = [
        data?.agent?.name ? `agent: ${data.agent.name}` : null,
        data?.gateway?.name ? `gateway: ${data.gateway.name}` : null,
        data?.terminal?.name ? `terminal: ${data.terminal.name}` : null,
    ]
        .filter(Boolean)
        .join('; ');


    const map = {
        merchant: data?.terminal?.name,
        gateway: data?.gateway?.name,
        agent: agentDisplay,
    };
    return map[type] ?? '';
};

export const getTotalCommission = (id, data) => {
    return data[id];
};

export const getFeeByDimension = (dimension, data) => {
    if (dimension === 'fixed') {
        return centToCurrency(data.fee);
    } else if (dimension === 'percent') {
        return data?.fee ? `${data.fee}%` : '';
    }
};
export const getModifiedBy = (data) => {
    const firstName = data?.modifiedBy?.firstName || '';
    const lastName = data?.modifiedBy?.lastName || '';
    return `${firstName} ${lastName}`.trim();
};
